"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { ArrowRight, FileText } from "lucide-react";
import { track } from "../lib/track";

interface NewsletterProps {
  heading: string;
  text: string;
}

const NewsLetterComp: React.FC<NewsletterProps> = ({ heading, text }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  // "Already subscribed" is a friendly outcome, not a failure — it gets its own
  // state so it isn't rendered in the red error slot.
  const [notice, setNotice] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false); // prevent double submits
  const [hp, setHp] = useState(""); // honeypot — also sent to the server, which makes the real decision
  const mountedAt = useRef(Date.now()); // how long the form was on screen, for the server's timing check

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    setSuccess(false);

    // Honeypot (bots often fill hidden fields)
    if (hp) {
      track?.("newsletter_signup_bot_caught", {
        label: "Honeypot Triggered",
        component: "Newsletter Signup",
      }, {
        event: "newsletter_signup_bot_caught",
      });
      setSubmitting(false);
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      track?.("newsletter_signup_error", {
        label: "Invalid Email",
        component: "Newsletter Signup",
      }, {
        event: "newsletter_signup_error",
        meta: { reason: "invalid_email" },
      });
      setSubmitting(false);
      return;
    }

    // The server requires a name, so catch it here rather than let it come
    // back as a 400 in the error slot.
    if (!name.trim()) {
      setError("Please enter your name.");
      track?.("newsletter_signup_error", {
        label: "Missing Name",
        component: "Newsletter Signup",
      }, {
        event: "newsletter_signup_error",
        meta: { reason: "missing_name" },
      });
      setSubmitting(false);
      return;
    }

    try {
      const subscribe = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, hp, elapsedMs: Date.now() - mountedAt.current }),
      });

      const data = await subscribe.json().catch(() => ({}));

      if (!subscribe.ok) {
        throw new Error(data.message || "Failed to subscribe to newsletter");
      }

      if (data.alreadySubscribed) {
        setNotice("You're already on the list.");
      } else {
        setSuccess(true);
      }

      setEmail("");
      setName("");

      // track only on confirmed success
      track?.("newsletter_signup_success", {
        label: "Newsletter Signup",
        component: "Newsletter Footer",
      }, {
        event: "newsletter_signup_success",
        label: "Newsletter Signup",
        meta: { email },
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const fieldClass =
    "w-full rounded-xl border border-[var(--border-on-dark)] bg-[var(--bg-card-dark)] px-4 py-3 " +
    "text-[var(--text-on-dark)] placeholder:text-[var(--text-on-dark-faint)] transition " +
    "focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  return (
    <section aria-labelledby="newsletter-heading" className="px-4 py-8">
      <div className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl bg-[var(--burgundy)] shadow-2xl">
        {/* Decoration: hairline along the top edge, warm glow bleeding in from
            the corner. Both purely visual — the block reads the same without them. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--honey)] to-transparent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-[var(--coral)] opacity-20 blur-3xl"
        />

        <div className="relative grid gap-7 p-5 sm:p-8 md:grid-cols-[1.05fr_1fr] md:gap-10 md:p-10">
          {/* Pitch */}
          <div className="text-left">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="h-px w-8 bg-[var(--honey)]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--apricot-lt)]">
                Free recipe download
              </span>
            </div>

            <h2
              id="newsletter-heading"
              className="mt-4 text-2xl font-extrabold leading-tight text-[var(--text-on-dark)] sm:text-3xl md:text-4xl"
            >
              {heading || "Join our newsletter"}
            </h2>

            <p className="mt-3 max-w-sm text-base text-[var(--text-on-dark-muted)]">
              {text || "Seasonal recipes, practical health notes, and the occasional offer."}
            </p>

            {/* CF's own photo of the bake, lifted from the recipe PDF itself.
                Sits in the footer on every page, so it must stay lazy — it is
                never the LCP element. */}
            <figure className="mt-6">
              <Image
                src="/images/BodhiFlapjacks.webp"
                alt="A tray of Bodhi Flapjack raw energy bars on a wooden board, topped with raw chocolate, nuts and seeds"
                width={900}
                height={675}
                sizes="(min-width: 768px) 420px, 92vw"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lg ring-1 ring-white/10 sm:aspect-[3/2]"
              />
              <figcaption className="mt-2 text-xs text-[var(--text-on-dark-faint)]">
                Bodhi Flapjack &mdash; the recipe we&rsquo;ll send you.
              </figcaption>
            </figure>
          </div>

          {/* Form */}
          <div className="rounded-2xl border border-[var(--border-on-dark)] bg-[var(--bg-glass-dark)] p-4 backdrop-blur sm:p-6 md:self-center">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[var(--border-on-dark)] text-[var(--apricot-lt)]">
                <FileText aria-hidden="true" className="h-4 w-4" />
              </span>
              <p className="text-sm leading-relaxed text-[var(--text-on-dark)]">
                Sign up and we&rsquo;ll email you our{" "}
                <strong className="font-semibold">Bodhi Flapjack recipe</strong> &mdash; a
                printable one-page PDF.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="mt-5">
              {/* Honeypot. Positioned off-screen rather than display:none — a bot that
                  parses the DOM skips hidden fields, but fills visible-to-CSS ones.
                  Named so a form-filler wants to complete it; autoComplete off so a real
                  browser never does. */}
              <input
                type="text"
                name="contact_website"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                className="absolute -left-[9999px] h-0 w-0 opacity-0"
                aria-hidden="true"
              />

              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                autoComplete="email"
                className={fieldClass}
                aria-invalid={!!error}
              />

              <label htmlFor="newsletter-name" className="sr-only">
                Full name
              </label>
              <input
                id="newsletter-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                className={`${fieldClass} mt-3`}
                aria-invalid={!!error}
              />

              <div aria-live="polite">
                {error && <p className="mt-3 text-sm text-red-300">{error}</p>}
                {notice && (
                  <p className="mt-3 text-sm text-[var(--text-on-dark-muted)]">{notice}</p>
                )}
                {success && (
                  <p className="mt-3 text-sm text-[var(--apricot-lt)]">
                    Check your inbox &mdash; the recipe is on its way.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                aria-busy={submitting}
                className="group mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent)] px-4 py-3 font-semibold text-[var(--on-lead)] transition hover:bg-[var(--coral-dk)] disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Subscribe"}
                {!submitting && (
                  <ArrowRight
                    aria-hidden="true"
                    className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-0.5"
                  />
                )}
              </button>
            </form>

            <p className="mt-4 text-xs text-[var(--text-on-dark-faint)]">
              We&rsquo;ll never share your address. Unsubscribe in one click.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsLetterComp;
